/**
 * TourProvider — global context that owns onboarding-tour state, lazily
 * mounts react-joyride when a tour is active, and persists terminal status
 * to the user_onboarding table.
 *
 * Wired into <App /> above the routed tree. Consumers:
 *   - `useTour()` to access startTour / restartTour / setDontShowAgain
 *   - `useFoundationalTour()` to auto-start the foundational v1 tour for
 *     users without a terminal user_onboarding row at (foundational, v1)
 *
 * Aesthetic: Shadcn-aligned styles via CSS variables. CSS vars handle dark
 * mode automatically because the variables themselves invert.
 *
 * Persistence: on terminal status (FINISHED / SKIPPED / closed) the
 * provider UPSERTs (user_id, tour_id, version) → status into
 * public.user_onboarding. RLS scopes the row to the calling user.
 */

import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
// Pull only TYPES from react-joyride here. Importing the runtime constants
// (EVENTS, STATUS) statically would defeat the React.lazy split below — Vite
// would emit them in the main bundle. We mirror the small set of string
// values we need so `import('react-joyride')` only happens when the lazy
// component renders.
import type { ButtonType, EventData, Step } from 'react-joyride';

// Mirrors react-joyride's STATUS / EVENTS string-literal exports for the
// values we react to. Values lifted directly from the v3 d.ts and verified
// against the runtime; they are stable across patch releases.
const RJ_STATUS_FINISHED = 'finished';
const RJ_STATUS_SKIPPED = 'skipped';
const RJ_EVENT_TOUR_END = 'tour:end';
const RJ_EVENT_TARGET_NOT_FOUND = 'error:target_not_found';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { captureError } from '@/lib/sentry';

// Lazy-load the Joyride bundle so it doesn't block first paint. react-joyride
// 3.x exposes `Joyride` as a named export, so we wrap it as a default export
// in the dynamic-import factory.
const LazyJoyride = lazy(async () => {
  const mod = await import('react-joyride');
  return { default: mod.Joyride };
});

export type TourFinishStatus =
  | 'completed'
  | 'dismissed'
  | 'skipped'
  | 'dismissed_permanent';

interface ActiveTour {
  tourId: string;
  version: string;
  steps: Step[];
}

interface TourContextValue {
  /** Start a tour. Replaces any currently-active tour. */
  startTour: (tourId: string, version: string, steps: Step[]) => void;
  /** Re-run the most recently seen tour for this user (e.g., from the Help menu). */
  restartTour: () => void;
  /** Whether a tour is currently active. */
  isRunning: boolean;
  /** "Don't show again" toggle state — read by the terminal-status mapper. */
  dontShowAgain: boolean;
  setDontShowAgain: (next: boolean) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTour must be used inside <TourProvider>');
  }
  return ctx;
}

/**
 * Shadcn-aligned joyride styles. CSS vars handle dark mode automatically.
 *
 * react-joyride v3 split theme tokens (primaryColor, backgroundColor, etc.)
 * out of the `Styles` interface and into a separate `Options` interface
 * exposed on `<Joyride>` via the top-level `options` prop. Nesting them under
 * `styles.options` (the v2 shape) silently dropped the values, leaving the
 * tour with default colors regardless of theme. We pass `styles` and
 * `options` as two distinct props now.
 *
 * Note: `buttonPrimary` is the v3 key for the Next/Last button — v2's
 * `buttonNext` is no longer recognised.
 */
const joyrideStyles = {
  tooltip: { borderRadius: 'var(--radius)' },
  buttonPrimary: { borderRadius: 'var(--radius)' },
  buttonBack: { borderRadius: 'var(--radius)' },
};

const joyrideOptions = {
  primaryColor: 'hsl(var(--primary))',
  backgroundColor: 'hsl(var(--background))',
  textColor: 'hsl(var(--foreground))',
  arrowColor: 'hsl(var(--background))',
  overlayColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 10000,
  showProgress: true,
  // Show the Skip button on every step (the v3 equivalent of the
  // v2 `showSkipButton` prop). Default is ['back', 'close',
  // 'primary'] — we swap 'close' for 'skip' so users always have
  // a visible escape hatch (per the skill's "Skip + Don't show
  // again" requirement).
  buttons: ['back', 'skip', 'primary'] as ButtonType[],
  overlayClickAction: 'close' as const,
};

const TERMINAL_STATUSES: string[] = [RJ_STATUS_FINISHED, RJ_STATUS_SKIPPED];

interface TourProviderProps {
  children: ReactNode;
}

/**
 * Persist a terminal tour result to public.user_onboarding. UPSERT on
 * (user_id, tour_id, version) so the "Don't show again" upgrade path
 * overwrites a prior 'dismissed' or 'skipped' row.
 */
async function persistTourResult(params: {
  userId: string;
  workspaceId: string | null;
  tourId: string;
  version: string;
  status: TourFinishStatus;
}): Promise<void> {
  const { userId, workspaceId, tourId, version, status } = params;

  // workspace_id is NOT NULL in the schema. If we somehow don't have one
  // yet (auth resolved but workspaces still loading), skip persistence —
  // the caller will re-prompt next session, which is the safe default.
  if (!workspaceId) {
    if (import.meta.env.DEV) {
      console.warn(
        '[TourProvider] no workspace_id available; skipping persist',
        { userId, tourId, version, status },
      );
    }
    return;
  }

  try {
    const { error } = await supabase
      .from('user_onboarding')
      .upsert(
        {
          user_id: userId,
          workspace_id: workspaceId,
          tour_id: tourId,
          version,
          status,
          completed_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,tour_id,version' },
      );
    if (error) throw error;
  } catch (err) {
    console.error('Error persisting tour result:', err);
    captureError(err as Error, { feature: 'onboarding-tour-persist' });
    // Non-fatal — the user already finished the tour. Worst case we
    // re-prompt them next session.
  }
}

export function TourProvider({ children }: TourProviderProps) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();

  const [active, setActive] = useState<ActiveTour | null>(null);
  const [run, setRun] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Remember the most recent tour so restartTour() can re-run it.
  const lastTourRef = useRef<ActiveTour | null>(null);

  const startTour = useCallback(
    (tourId: string, version: string, steps: Step[]) => {
      const next = { tourId, version, steps };
      lastTourRef.current = next;
      setDontShowAgain(false);
      setActive(next);
      setRun(true);
    },
    [],
  );

  const restartTour = useCallback(() => {
    const last = lastTourRef.current;
    if (!last) return;
    setDontShowAgain(false);
    setActive(last);
    setRun(true);
  }, []);

  const handleEvent = useCallback(
    (data: EventData) => {
      const { type, status, action, origin } = data;

      // TARGET_NOT_FOUND surfaces a missing data-tour-id selector — the
      // most common breakage source for tours after a refactor. Surface
      // in dev so it's noticed; do not capture to GlitchTip (per skill
      // guidance, tier-3 best-effort). End the tour cleanly so a missing
      // target never leaves us in a running-but-invisible state (e.g., a
      // step targeting a contact's phone row when the contact has no
      // phones — the click-to-dial step). Without this, run stays true
      // and active stays non-null, orphaning the overlay.
      if (type === RJ_EVENT_TARGET_NOT_FOUND) {
        if (import.meta.env.DEV) {
          console.warn(
            `[TourProvider] target not found for tour=${active?.tourId} version=${active?.version}`,
            data,
          );
        }
        setRun(false);
        setActive(null);
        return;
      }

      if (type !== RJ_EVENT_TOUR_END) return;
      if (!TERMINAL_STATUSES.includes(status)) return;

      // Map joyride's terminal state to our enum. "Don't show again" wins
      // whenever the checkbox is ticked, regardless of whether the user
      // pressed Finish or Skip.
      let mapped: TourFinishStatus;
      if (dontShowAgain) {
        mapped = 'dismissed_permanent';
      } else if (status === RJ_STATUS_FINISHED) {
        mapped = 'completed';
      } else if (
        status === RJ_STATUS_SKIPPED ||
        action === 'skip' ||
        origin === 'button_skip'
      ) {
        mapped = 'skipped';
      } else {
        mapped = 'dismissed';
      }

      setRun(false);
      const finishedTour = active;
      setActive(null);

      if (user && finishedTour) {
        void persistTourResult({
          userId: user.id,
          workspaceId: currentWorkspace?.id ?? null,
          tourId: finishedTour.tourId,
          version: finishedTour.version,
          status: mapped,
        });
      }
    },
    [active, dontShowAgain, user, currentWorkspace?.id],
  );

  // If the user signs out mid-tour, abort. Avoids an orphaned overlay.
  useEffect(() => {
    if (!user && run) {
      setRun(false);
      setActive(null);
    }
  }, [user, run]);

  const value = useMemo<TourContextValue>(
    () => ({
      startTour,
      restartTour,
      isRunning: run,
      dontShowAgain,
      setDontShowAgain,
    }),
    [startTour, restartTour, run, dontShowAgain],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && run && (
        <Suspense fallback={null}>
          <LazyJoyride
            steps={active.steps}
            run={run}
            continuous
            onEvent={handleEvent}
            styles={joyrideStyles}
            options={joyrideOptions}
            scrollToFirstStep
          />
        </Suspense>
      )}
    </TourContext.Provider>
  );
}
